<template>
  <div class="gr-root">
    <appHeader></appHeader>

    <transition appear name="fade">
      <div class="gr-layout container">

        <!-- ── Sidebar ── -->
        <RecognitionSidebar />

        <!-- ── Main Content ── -->
        <main class="gr-main">

          <div class="gr-page-header">
            <div>
              <h1 class="gr-title">Give Recognition</h1>
              <p class="gr-sub">Appreciate your colleagues and make their day!</p>
            </div>
            <a href="javascript:void(0)" @click="$router.push({ path: '/recognition'})" class="gr-back-btn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Back to Feed
            </a>
          </div>

          <div class="gr-form-card">

            <!-- ── Step 1: Select Employees (multi-select) ── -->
            <div class="gr-section">
              <div class="gr-section-label">
                <span class="gr-step-num">1</span>
                Select Employees
                <span class="gr-optional">(Add multiple)</span>
              </div>

              <!-- Search input — always visible so more people can be added -->
              <div class="gr-search-wrap">
                <svg class="gr-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="16" height="16">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  ref="searchInput"
                  class="gr-search-input"
                  type="text"
                  :placeholder="selectedEmployees.length ? 'Add another person...' : 'Search by name or email...'"
                  v-model="searchQuery"
                  @input="onSearch"
                  @focus="showDropdown = true"
                  @blur="onBlur"
                  autocomplete="off"
                />

                <!-- Autocomplete dropdown -->
                <div class="gr-dropdown" v-if="showDropdown && filteredEmployees.length">
                  <div
                    v-for="emp in filteredEmployees"
                    :key="emp.id"
                    class="gr-dropdown-item"
                    :class="{ 'gr-dropdown-item--selected': isSelected(emp.id) }"
                    @mousedown.prevent="toggleEmployee(emp)"
                  >
                    <img :src="emp.avatar" :alt="emp.name" class="gr-dd-avatar" />
                    <div class="gr-dd-info">
                      <span class="gr-dd-name">{{ emp?.name }}</span>
                    </div>
                    <!-- Checkmark when already selected -->
                    <span v-if="isSelected(emp.id)" class="gr-dd-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="14" height="14">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                  </div>
                </div>

                <div class="gr-dropdown gr-dropdown--empty" v-if="showDropdown && searchQuery && !filteredEmployees.length">
                  <p class="gr-dd-none">No employees found for "{{ searchQuery }}"</p>
                </div>
              </div>

              <!-- Selected employee chips -->
              <div v-if="selectedEmployees.length" class="gr-chips-wrap">
                <div
                  v-for="emp in selectedEmployees"
                  :key="emp.id"
                  class="gr-chip"
                >
                  <img :src="emp.avatar" :alt="emp.name" class="gr-chip-avatar" />
                  <span class="gr-chip-name">{{ emp.name }}</span>
                  <button class="gr-chip-remove" @click="removeEmployee(emp.id)" title="Remove">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="11" height="11">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>

            </div>

            <!-- ── Step 2: Select Category ── -->
            <div class="gr-section">
              <div class="gr-section-label">
                <span class="gr-step-num">2</span>
                Select Category
              </div>

              <div class="gr-categories">
                <button
                  v-for="cat in categories"
                  :key="cat.value"
                  class="gr-cat-btn"
                  :class="{ 'gr-cat-btn--active': selectedCategory === cat.value }"
                  :style="selectedCategory === cat.value ? { borderColor: cat.color, boxShadow: `0 0 0 3px ${cat.color}20` } : {}"
                  @click="selectedCategory = cat.value"
                >
                  <span class="gr-cat-icon" :style="{ background: cat.bg, color: cat.color }" v-html="cat.icon"></span>
                  <span class="gr-cat-label">{{ cat.label }}</span>
                </button>
              </div>
            </div>

            <!-- ── Step 2: Add Message ── -->
            <div class="gr-section">
              <div class="gr-section-label">
                <span class="gr-step-num">3</span>
                Add Message
                <span class="gr-optional">(Optional)</span>
              </div>

              <div class="gr-textarea-wrap">
                <textarea
                  class="gr-textarea"
                  placeholder="Write something meaningful — share what they did and why it matters... 🌟"
                  v-model="message"
                  maxlength="250"
                  rows="4"
                ></textarea>
                <span class="gr-char-count">{{ message.length }}/250</span>
              </div>
            </div>

            <!-- ── Step 3: Select Points ── -->
            <div class="gr-section">
              <div class="gr-section-label">
                <span class="gr-step-num">4</span>
                Select Points
              </div>

              <div class="gr-points-row">
                <button
                  v-for="opt in pointOptions"
                  :key="opt.value"
                  class="gr-pts-btn"
                  :class="{ 'gr-pts-btn--active': selectedPoints === opt.value && !customPointsActive }"
                  @click="selectPoints(opt.value)"
                >
                  {{ opt.label }}
                </button>

                <!-- Custom points -->
                <div class="gr-pts-custom" :class="{ 'gr-pts-custom--active': customPointsActive }">
                  <input
                    type="number"
                    class="gr-pts-input"
                    placeholder="Custom"
                    v-model.number="customPoints"
                    min="1"
                    :max="balance"
                    @focus="customPointsActive = true"
                    @blur="onCustomBlur"
                  />
                </div>
              </div>

              <div class="gr-balance-notes">
                <div class="gr-balance-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="13" height="13">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Available Balance: <strong>{{ balance.toLocaleString() }} pts</strong>
                </div>
                <div class="gr-balance-note gr-balance-note--cost" v-if="selectedEmployees.length > 1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="13" height="13">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  Total cost: <strong>{{ totalPointsCost.toLocaleString() }} pts</strong>
                  <span class="gr-cost-breakdown">({{ effectivePoints }} × {{ selectedEmployees.length }} people)</span>
                </div>
              </div>
            </div>

            <!-- ── Actions ── -->
            <div class="gr-actions">
              <a href="javacript:void(0)"  @click="$router.push({ path: '/recognition'})" class="gr-cancel-btn">Cancel</a>
              <button
                class="gr-submit-btn"
                :disabled="!canSubmit || isSubmitting"
                :class="{ 'gr-submit-btn--disabled': !canSubmit || isSubmitting }"
                @click="sendRecognition"
              >
                <!-- Spinner shown while submitting -->
                <svg v-if="isSubmitting" class="gr-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="15" height="15">
                  <circle cx="12" cy="12" r="10" stroke-opacity="0.3"/>
                  <path d="M12 2a10 10 0 0 1 10 10"/>
                </svg>
                <!-- Send icon shown normally -->
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                {{ isSubmitting ? 'Sending...' : 'Send Recognition' }}
              </button>
            </div>

          </div>

        </main>

        <!-- ── Right panel ── -->
        <aside class="gr-panel">
          <div class="gr-panel-sticky">

            <div class="rf-widget">
              <p class="rf-widget-label">My Recognition Balance</p>
              <div class="rf-balance-row">
                <span class="rf-balance-num">{{ balance.toLocaleString() }}</span>
                <span class="rf-balance-unit">pts</span>
              </div>
              <p class="rf-balance-sub">Recognitions Points</p>
              <button class="rf-store-btn" @click="$router.push({ path: '/shop/category/1'})">Start Shopping</button>
            </div>

            <div class="gr-widget gr-tips-widget">
              <p class="gr-widget-title">💡 Recognition Tips</p>
              <ul class="gr-tips-list">
                <li>Be specific about what they did</li>
                <li>Mention the impact it had</li>
                <li>Keep it genuine and personal</li>
                <li>Even small acts deserve recognition</li>
              </ul>
            </div>

          </div>
        </aside>

      </div>
    </transition>
    <!-- Toast Notification Component -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :duration="3000"
      @close="toast.show = false"
    />
    <appFooter class="gr-footer-desktop"></appFooter>
    <navigationFooter class="gr-footer-mobile"></navigationFooter>
  </div>
</template>

<script>
// ── Replace the entire <script> block with this ──────────

import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import navigationFooter from "@/components/navigationFooter.vue";
import RecognitionSidebar from "./RecognitionSidebar.vue";
import RecognitionService from "@/services/RecognitionService";
import ToastNotification from "@/components/ToastNotification.vue";

export default {
  name: "GiveRecognition",

  components: {
    appHeader: Header,
    appFooter: Footer,
    navigationFooter,
    RecognitionSidebar,
    ToastNotification
  },

  data() {
    return {
      toast: {
        show: false,
        message: "",
        type: "info"
      },
      // ── UI state ─────────────────────────────────────
      isSubmitting:  false,
      isLoadingUsers: false,
      isLoadingQuota: false,
      submitError:   null,
      submitSuccess: false,

      // ── Search & employee selection ───────────────────
      searchQuery:       "",
      showDropdown:      false,
      selectedEmployees: [],
      employees:         [],        // loaded from API

      // ── Form fields ───────────────────────────────────
      message:          "",
      selectedCategory: null,
      selectedPoints:   20,
      customPointsActive: false,
      customPoints:     null,

      // ── Balance (loaded from quota API) ──────────────
      balance: 0,

      // ── Categories (static — no API needed) ──────────
      categories: [
        {
          value: "teamwork",
          label: "Teamwork",
          color: "#3b82f6",
          bg: "#eff6ff",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
        },
        {
          value: "leadership",
          label: "Leadership",
          color: "#f59e0b",
          bg: "#fffbeb",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
        },
        {
          value: "innovation",
          label: "Innovation",
          color: "#8b5cf6",
          bg: "#f5f3ff",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><line x1="12" y1="2" x2="12" y2="6"/><path d="M12 22v-4"/><path d="M9 18h6"/><path d="M8 6a4 4 0 0 0 4 10 4 4 0 0 0 4-10"/></svg>`,
        },
        {
          value: "helping",
          label: "Support",
          color: "#10b981",
          bg: "#f0fdf4",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`,
        },
        {
          value: "excellence",
          label: "Excellence",
          color: "#f43f5e",
          bg: "#fff1f2",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><circle cx="12" cy="8" r="6"/><path d="M8.21 13.89L7 22l5-3 5 3-1.21-8.11"/></svg>`,
        },
        {
          value: "growth",
          label: "Growth",
          color: "#0ea5e9",
          bg: "#f0f9ff",
          icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" width="22" height="22"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>`,
        },
      ],

      pointOptions: [
        { label: "10 pts",  value: 10  },
        { label: "20 pts",  value: 20  },
        { label: "50 pts",  value: 50  },
        { label: "100 pts", value: 100 },
      ],
    };
  },

  computed: {
    filteredEmployees() {
      if (!this.searchQuery.trim()) return [];

      const q = this.searchQuery.trim().toLowerCase();

      return this.employees
        .filter((e) => {
          const name = (e.name || "").toLowerCase();
          const email = (e.email || "").toLowerCase();

          return (
            name.includes(q) ||
            email.includes(q)
          );
        })
        .slice(0, 6);
    },

    effectivePoints() {
      if (this.customPointsActive && this.customPoints > 0) return this.customPoints;
      return this.selectedPoints;
    },

    totalPointsCost() {
      return this.effectivePoints * this.selectedEmployees.length;
    },

    canSubmit() {
      return (
        !this.isSubmitting &&
        this.selectedEmployees.length > 0 &&
        this.selectedCategory !== null &&
        this.effectivePoints > 0 &&
        this.effectivePoints <= this.balance
      );
    },
  },

  methods: {
    // ── Load quota on mount ───────────────────────────────
    async loadQuota() {
      this.isLoadingQuota = true;
      try {
        const resp = await RecognitionService.getMyQuota();
        this.balance = resp.data.data.available_points ?? 0;
      } catch (err) {
        console.error("Failed to load quota:", err);
        this.balance = 0;
      } finally {
        this.isLoadingQuota = false;
      }
    },

    // ── Load all users for client on mount ────────────────
    async loadUsers() {
      this.isLoadingUsers = true;
      try {
        const resp = await RecognitionService.getUsers();
        this.employees = resp.data.data ?? [];
      } catch (err) {
        console.error("Failed to load users:", err);
        this.employees = [];
      } finally {
        this.isLoadingUsers = false;
      }
    },

    // ── Search input handler ──────────────────────────────
    onSearch() {
      this.showDropdown = true;
    },

    onBlur() {
      setTimeout(() => { this.showDropdown = false; }, 150);
    },

    // ── Employee selection ────────────────────────────────
    isSelected(id) {
      return this.selectedEmployees.some((e) => e.id === id);
    },

    toggleEmployee(emp) {
      if (this.isSelected(emp.id)) {
        this.removeEmployee(emp.id);
      } else {
        this.selectedEmployees.push(emp);
      }
      this.searchQuery = "";
      this.$nextTick(() => {
        if (this.$refs.searchInput) this.$refs.searchInput.focus();
      });
    },

    removeEmployee(id) {
      this.selectedEmployees = this.selectedEmployees.filter((e) => e.id !== id);
    },

    // ── Points selection ──────────────────────────────────
    selectPoints(val) {
      this.selectedPoints      = val;
      this.customPointsActive  = false;
      this.customPoints        = null;
    },

    onCustomBlur() {
      if (!this.customPoints) this.customPointsActive = false;
    },
    showToast(message, type = "info") {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },

    // ── Submit ────────────────────────────────────────────
    async sendRecognition() {
      if (!this.canSubmit) return;

      this.isSubmitting = true;
      this.submitError  = null;
      this.submitSuccess = false;

      const payload = {
        recipient_ids: this.selectedEmployees.map((e) => e.id),
        points:        this.effectivePoints,
        message:       this.message,
        category:      this.selectedCategory,
      };

      try {
        const resp = await RecognitionService.giveRecognition(payload);

        if (resp.data.success) {
          this.submitSuccess = true;
          this.showToast(
            resp.data.message || "Recognition points awarded successfully.",
            "success"
          );
          // Brief success moment then redirect to feed
          setTimeout(() => {
            this.$router.push("/recognition");
          }, 500);

        } else {
          // API returned success: false with a message
          this.showToast(resp.data.message || "Failed to send recognition.", "error");
        }

      } catch (err) {
        // HTTP error (4xx / 5xx)
        const data = err.response?.data;

        if (err.response?.status === 422 && data?.errors) {
          // Validation errors — join all messages
          const messages = Object.values(data.errors).flat();
          this.submitError = messages.join(" ");

        } else if (err.response?.status === 422 && data?.data?.available_points !== undefined) {
          // Insufficient balance
          this.submitError =
            `Insufficient balance. Available: ${data.data.available_points} pts, Required: ${data.data.required_points} pts.`;

        } else if (err.response?.status === 403) {
          this.submitError = data?.message || "You are not authorized to give recognition.";

        } else {
          this.submitError = data?.message || "Something went wrong. Please try again.";
        }
        this.showToast(this.submitError || "Failed to send recognition.", "error");
      } finally {
        this.isSubmitting = false;
      }
    },
  },

  created() {
    this.loadQuota();
    this.loadUsers();
  },
};
</script>

<style lang="scss" scoped>

// ─────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────
.gr-root {
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.gr-footer-desktop { display: block; }
.gr-footer-mobile  {
  visibility: hidden;
  pointer-events: none;
  height: 0;
  overflow: hidden;
}

// ─────────────────────────────────────────────────────────
// Layout grid (matches RecognitionFeed exactly)
// ─────────────────────────────────────────────────────────
.gr-layout {
  display: grid;
  grid-template-columns: 220px 1fr 280px;
  align-items: stretch;
  padding-top: 2rem;
  padding-bottom: 3rem;
  flex: 1;
}

.gr-main {
  padding: 0 2rem;
  min-width: 0;
}

// ─────────────────────────────────────────────────────────
// Page header
// ─────────────────────────────────────────────────────────
.gr-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.gr-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px;
  line-height: 1.2;
}

.gr-sub {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

.gr-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.85rem;
  font-weight: 500;
  color: $primary;
  text-decoration: none;
  padding: 7px 14px;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  background: #f0f7ff;
  white-space: nowrap;
  transition: background 0.12s ease;

  svg { stroke: $primary; }

  &:hover {
    background: #dbeafe;
    text-decoration: none;
    color: $primary;
  }
}

// ─────────────────────────────────────────────────────────
// Form card
// ─────────────────────────────────────────────────────────
.gr-form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: visible;
}

// ─────────────────────────────────────────────────────────
// Section
// ─────────────────────────────────────────────────────────
.gr-section {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
}

.gr-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1rem;
}

.gr-step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: $primary;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 50%;
  flex-shrink: 0;
}

.gr-optional {
  font-size: 0.78rem;
  font-weight: 400;
  color: #9ca3af;
}

// ─────────────────────────────────────────────────────────
// Selected employee chips
// ─────────────────────────────────────────────────────────
.gr-chips-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.gr-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 4px 10px 4px 5px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #1d4ed8;
  transition: background 0.12s ease;
}

.gr-chip-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.gr-chip-name {
  white-space: nowrap;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gr-chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #93c5fd;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: color 0.12s, background 0.12s;

  &:hover {
    color: #ef4444;
    background: #fee2e2;
  }
}

// ─────────────────────────────────────────────────────────
// Employee search
// ─────────────────────────────────────────────────────────
.gr-search-wrap {
  position: relative;
}

.gr-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  stroke: #9ca3af;
  pointer-events: none;
}

.gr-search-input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px 10px 38px;
  font-size: 0.875rem;
  color: #111827;
  background: #fff;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  outline: none;

  &::placeholder { color: #9ca3af; }

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

// Autocomplete dropdown
.gr-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;

  &--empty { padding: 1rem; }
}

.gr-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover { background: #f0f7ff; }
  &:not(:last-child) { border-bottom: 1px solid #f3f4f6; }

  // Already-selected: light tinted bg + checkmark
  &--selected {
    background: #f0f7ff;

    .gr-dd-name { color: $primary; font-weight: 600; }
  }
}

.gr-dd-check {
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: $primary;
  border-radius: 50%;

  svg { stroke: #fff; }
}

.gr-dd-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.gr-dd-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.gr-dd-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.gr-dd-dept {
  font-size: 0.75rem;
  color: #6b7280;
}

.gr-dd-none {
  font-size: 0.85rem;
  color: #9ca3af;
  margin: 0;
  text-align: center;
}

// Selected employee chip
.gr-selected-emp {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f0f7ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 10px 14px;
}

.gr-selected-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.gr-selected-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.gr-selected-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1d4ed8;
}

.gr-selected-dept {
  font-size: 0.78rem;
  color: #3b82f6;
}

.gr-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #9ca3af;
  border-radius: 4px;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;

  &:hover { color: #ef4444; background: #fee2e2; }
}

// ─────────────────────────────────────────────────────────
// Message textarea
// ─────────────────────────────────────────────────────────
.gr-textarea-wrap {
  position: relative;
}

.gr-textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.875rem;
  color: #111827;
  line-height: 1.6;
  resize: vertical;
  min-height: 100px;
  outline: none;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  box-sizing: border-box;

  &::placeholder { color: #9ca3af; }

  &:focus {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

.gr-char-count {
  position: absolute;
  bottom: 10px;
  right: 12px;
  font-size: 0.72rem;
  color: #9ca3af;
  pointer-events: none;
}

// ─────────────────────────────────────────────────────────
// Points
// ─────────────────────────────────────────────────────────
.gr-points-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}

.gr-pts-btn {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 7px 18px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  background: #fff;
  cursor: pointer;
  transition: all 0.12s ease;
  white-space: nowrap;

  &:hover {
    border-color: $primary;
    color: $primary;
    background: #f0f7ff;
  }

  &--active {
    background: $primary;
    border-color: $primary;
    color: #fff;
    font-weight: 600;

    &:hover { background: darken($primary, 6%); }
  }
}

.gr-pts-custom {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
  transition: border-color 0.12s ease;

  &--active {
    border-color: $primary;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);
  }
}

.gr-pts-input {
  border: none;
  outline: none;
  padding: 7px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  width: 90px;
  background: transparent;

  &::placeholder { color: #9ca3af; }
  // Hide number arrows
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
}

.gr-balance-notes {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gr-balance-note {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.8rem;
  color: #6b7280;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 6px;
  padding: 5px 10px;

  svg { stroke: #16a34a; flex-shrink: 0; }
  strong { color: #15803d; }

  &--cost {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;

    svg { stroke: $primary; }
    strong { color: $primary; }
  }
}

.gr-cost-breakdown {
  font-size: 0.72rem;
  color: #60a5fa;
  margin-left: 2px;
}

// ─────────────────────────────────────────────────────────
// Actions
// ─────────────────────────────────────────────────────────
.gr-actions {
  padding: 1.25rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  border-top: 1px solid #f3f4f6;
}

.gr-cancel-btn {
  display: inline-flex;
  align-items: center;
  padding: 9px 22px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover { background: #f9fafb; text-decoration: none; color: #374151; }
}

.gr-submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 24px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: $primary;
  border: none;
  border-radius: 7px;
  cursor: pointer;
  transition: background 0.15s ease;

  svg { stroke: #fff; }

  &:hover { background: darken($primary, 8%); }

  &--disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }
}

// ─────────────────────────────────────────────────────────
// Right panel
// ─────────────────────────────────────────────────────────
.gr-panel {
  padding-left: 1.5rem;
  border-left: 1px solid #e5e7eb;
}

.gr-panel-sticky {
  position: sticky;
  top: 80px;
}

.gr-widget {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 0.875rem;

  &:last-child { margin-bottom: 0; }
}

.gr-widget-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 6px;
}

.gr-balance-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 2px;
}

.gr-balance-num {
  font-size: 2.1rem;
  font-weight: 700;
  color: $primary;
  line-height: 1;
}

.gr-balance-unit {
  font-size: 0.95rem;
  color: #9ca3af;
}

.gr-balance-sub {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0;
}

.gr-widget-title {
  font-size: 0.78rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 10px;
}

.gr-tips-list {
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.82rem;
    color: #4b5563;
    line-height: 1.4;

    &::before {
      content: "✓";
      font-size: 0.72rem;
      font-weight: 700;
      color: #16a34a;
      background: #f0fdf4;
      border-radius: 50%;
      width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
    }
  }
}

// ─────────────────────────────────────────────────────────
// Tablet
// ─────────────────────────────────────────────────────────
@media (max-width: 1100px) {
  .gr-layout {
    grid-template-columns: 210px 1fr;
    grid-template-areas:
      "sidebar main"
      "panel   panel";
    align-items: start;
  }

  .gr-panel {
    grid-area: panel;
    border-left: none;
    border-top: 1px solid #e5e7eb;
    padding-left: 0;
    padding-top: 1.25rem;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 0.875rem;
  }

  .gr-panel-sticky {
    position: static;
    display: contents;
  }

  .gr-widget { margin-bottom: 0; }
}

// ─────────────────────────────────────────────────────────
// Mobile
// ─────────────────────────────────────────────────────────
@media (max-width: 768px) {
  .gr-footer-desktop { display: none !important; }
  .gr-footer-mobile  {
    visibility: visible !important;
    pointer-events: auto !important;
    height: auto !important;
    overflow: visible !important;
  }

  .gr-layout {
    display: block;
    padding: 0;
  }

  .gr-main {
    padding: 1rem 1rem 90px;
  }

  .gr-panel { display: none; }

  .gr-page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .gr-back-btn { align-self: flex-start; }

  .gr-section { padding: 1.25rem 1rem; }

  .gr-actions {
    padding: 1rem;
    flex-direction: column-reverse;
    gap: 0.5rem;
  }

  .gr-cancel-btn,
  .gr-submit-btn {
    width: 100%;
    justify-content: center;
  }

  .gr-points-row { gap: 0.4rem; }
}

// ─────────────────────────────────────────────────────────
// Category selector
// ─────────────────────────────────────────────────────────
.gr-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem;
}

.gr-cat-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.12s ease;
  min-width: 80px;

  &:hover {
    border-color: $primary;
    background: #f0f7ff;
  }

  &--active {
    border-color: $primary;
    background: #eff6ff;
    box-shadow: 0 0 0 3px rgba($primary, 0.1);

    .gr-cat-label { color: $primary; font-weight: 600; }
  }
}

// After
.gr-cat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 10px;
  flex-shrink: 0;

  svg { stroke: currentColor; }
}

.gr-cat-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .gr-categories { gap: 0.5rem; }

  .gr-cat-btn {
    padding: 10px 12px;
    min-width: 68px;
  }

  .gr-cat-icon { font-size: 1.2rem; }
}

// After
.gr-cat-btn--active {
  border-width: 2px;
  box-shadow: 0 0 0 3px rgba($primary, 0.08);

  .gr-cat-label { font-weight: 600; }
}
.gr-spinner {
  animation: gr-spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes gr-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.rf-widget {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 1.1rem 1.2rem;
  margin-bottom: 0.875rem;

  &:last-child { margin-bottom: 0; }
}

.rf-widget-label {
  font-size: 0.78rem;
  font-weight: 500;
  color: #6b7280;
  margin: 0 0 6px;
}

.rf-balance-row {
  display: flex;
  align-items: baseline;
  gap: 5px;
  margin-bottom: 2px;
}

.rf-balance-num {
  font-size: 2.1rem;
  font-weight: 700;
  color: $primary;
  line-height: 1;
}

.rf-balance-unit {
  font-size: 0.95rem;
  color: #9ca3af;
}

.rf-balance-sub {
  font-size: 0.75rem;
  color: #9ca3af;
  margin: 0 0 1rem;
}

.rf-store-btn {
  width: 100%;
  background: $primary;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: darken($primary, 8%); }
}

.rf-widget-title {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 10px;
}

.rf-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
}

.rf-row-label { font-size: 0.85rem; color: #4b5563; }

.rf-row-val {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;

  &--green { color: #16a34a; }
}
</style>